import React from 'react';
import { renderRule, StructuredText } from "react-datocms";
import { isBlockquote, isHeading, isLink, isList, isListItem, isParagraph } from "datocms-structured-text-utils";
import Paragraph from "components/elements/Paragraph";
import { IHeadings } from "components/elements/Heading";
import { formatToDashedString } from "helpers";
import Media from "components/elements/Media";
import { ITextSize } from "types/base";

type Props = {
    content: any;
    paragraphSize?: ITextSize;
    paragraphClassName?: string,
    headingClassName?: string,
};

export default function Content(props: Props) {
    const { content, paragraphSize, paragraphClassName, headingClassName } = props;

    const paragraph = [
        renderRule(
            isParagraph,
            ({ adapter: { renderNode }, node, children, key, ancestors }) => {
                return (
                    <Paragraph
                        key={key}
                        theme={'default'}
                        fontWeight={"light"}
                        size={paragraphSize ? paragraphSize : 'small'}
                        className={paragraphClassName ? paragraphClassName : ''}
                    >
                        {children}
                    </Paragraph>
                );
            }
        )
    ];

    const list = [
        renderRule(
            isList,
            ({ adapter: { renderNode }, node, children, key, ancestors }) => {
                return (
                    <ul key={key} className={"mt-4 list-disc"}>{children}</ul>
                );
            }
        )
    ];

    const listItem = [
        renderRule(
            isListItem,
            ({ adapter: { renderNode }, node, children, key, ancestors }) => {
                return (
                    <li className={'w-full pb-4 ml-4 mt-1'} key={key}>
                        <div className={'flex-1'}>{children}</div>
                    </li>
                );
            }
        )
    ];

    const heading = [
        renderRule(
            isHeading,
            ({ adapter: { renderNode }, node, children, key, ancestors }) => {

                const headingType = `h${node.level}` as IHeadings;

                // TODO: PLEASE REFACTOR THIS
                const getHeadingId = () => {
                    if (typeof children[0] === "string") {
                        return formatToDashedString(children[0]) as any;
                    } else if (typeof children[0]?.props?.children[0] === "string") {
                        return formatToDashedString(children[0]?.props?.children[0]) as any;
                    }

                    return "";
                };

                switch (headingType) {
                    case "h1":
                        return (
                            <h1
                                key={key}
                                id={getHeadingId()}
                                className={headingClassName ? headingClassName : "text-purple-dark font-light text-4xl mb-10 mt-10 mb-5"}
                            >
                                {children}
                            </h1>
                        );
                    case "h2":
                        return (
                            <h2
                                key={key}
                                id={getHeadingId()}
                                className={headingClassName ? headingClassName : "text-purple-dark font-medium text-2xl sm:text-2xl mb-5 mt-8"}
                            >
                                {children}
                            </h2>
                        );
                    case "h3":
                        return (
                            <h3
                                key={key}
                                id={getHeadingId()}
                                className={headingClassName ? headingClassName : "text-purple-dark font-light text-lg mb-2 lg:text-xl lg:mb-3 mt-10 mb-5"}
                            >
                                {children}
                            </h3>
                        );
                    case "h4":
                        return (
                            <h4
                                key={key}
                                id={getHeadingId()}
                                className={headingClassName ? headingClassName : "text-purple-dark font-light text-md mb-2 lg:text-lg lg:mb-3 mt-10 mb-5"}
                            >
                                {children}
                            </h4>
                        );
                    case "h5":
                        return (
                            <h5
                                key={key}
                                id={getHeadingId()}
                                className={headingClassName ? headingClassName : "text-purple-dark font-light text-xl mb-2 font-bold leading-loose"}
                            >
                                {children}
                            </h5>
                        );
                }
            }
        )
    ];

    const anchor = [
        renderRule(
            isLink,
            ({ adapter: { renderNode }, node, children, key, ancestors }) => {
                const isTargetBlank = node.meta?.find((node) => node.value == "_blank");

                return (
                    <a key={key} href={node.url} target={isTargetBlank ? isTargetBlank.value : null}>
                        <span className={"text-orange hover:underline"}>{children}</span>
                    </a>
                );
            }
        )
    ];

    const blockquote = [
        renderRule(
            isBlockquote,
            ({ adapter: { renderNode }, node, children, key, ancestors }) => {
                let quote = "";
                children.forEach((child) => quote = child.props.children);
                return (
                    <div
                        key={key}
                        className={"my-10 px-8 py-4 border-l-4 border-purple-tint-light text-purple-tint-dark font-thin text-lg"}
                    >
                        {quote}
                    </div>
                );
            }
        )
    ];

    const formatStructuredTextToParagraph = () => {
        return [
            ...paragraph,
            ...heading,
            ...anchor,
            ...blockquote,
            ...listItem,
            ...list,
        ];
    };

    const renderBlocks = ({ record }: any) => {
        switch (record.__typename) {
            case 'MediaObjectRecord':
                return (
                    <Media {...record} className={"my-10"}/>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <StructuredText
                data={content}
                customRules={formatStructuredTextToParagraph()}
                renderBlock={renderBlocks}
            />
        </>
    );
}